// Entra native authentication: the portal's own sign-in, sign-up and password-reset screens. MSAL's custom-auth
// client drives each flow as a chain of states (password needed, code needed, done), and talks to Entra through
// the API's pass-through (/api/v1/auth/native), because Entra's native API refuses calls straight from a browser.
// The signed-in account lands in the same browser cache as before, so a reload picks it up.
import { BrowserCacheLocation } from "@azure/msal-browser";
import {
  CustomAuthPublicClientApplication,
  type ICustomAuthPublicClientApplication,
  type SignUpAttributesRequiredState,
  type SignUpCodeRequiredState,
  type SignUpCompletedState,
  type SignUpPasswordRequiredState,
} from "@azure/msal-browser/custom-auth";
import type { AuthConfig, PortalConfig } from "../config";

export type NativeClient = ICustomAuthPublicClientApplication;

/** The company a new sign-up gave, kept until the first /workspace call creates their workspace with it. */
export const SIGNUP_COMPANY_KEY = "av-signup-company";

let client: Promise<NativeClient> | null = null;

/**
 * What a native sign-in asks for: the API's scope plus the OpenID ones. The SDK adds openid, profile and
 * offline_access only when no scopes are given; without openid Entra sends no ID token and the sign-in fails
 * (id_token_missing), and without offline_access no refresh token, so the session would end within the hour.
 */
export const signInScopes = (apiScope: string) => ["openid", "profile", "offline_access", apiScope];

/** Whether this environment signs in on the portal's own screens. */
export const usesNativeSignIn = (auth: AuthConfig) => !!(auth.enabled && auth.nativeSignIn && auth.authority && auth.clientId && auth.scope);

/** https://{host}/{tenant}/: the authority without its /v2.0, which the custom-auth client expects. */
function tenantAuthority(authority: string) {
  const url = new URL(authority);
  const tenant = url.pathname.split("/").filter(Boolean)[0] ?? "";
  return `${url.origin}/${tenant}/`;
}

/** One client per page load, made on first use. */
export function nativeClient(portal: PortalConfig, auth: AuthConfig): Promise<NativeClient> {
  client ??= CustomAuthPublicClientApplication.create({
    auth: {
      clientId: auth.clientId!,
      authority: tenantAuthority(auth.authority!),
      // Known, so MSAL takes the authority as given rather than asking Microsoft's instance discovery about it.
      knownAuthorities: [new URL(auth.authority!).host],
      // No postLogoutRedirectUri: signing out stays on the page, and the portal decides where to go next.
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    customAuth: {
      // Password and email code; "redirect" lets Entra say when a sign-in needs Microsoft's pages instead.
      challengeTypes: ["password", "oob", "redirect"],
      authApiProxyUrl: `${portal.apiUrl}/api/v1/auth/native`,
    },
  });
  return client;
}

/** The pieces of an SDK error the screens read. */
interface FlowError {
  isTokenExpired?: () => boolean;
  isRedirectRequired?: () => boolean;
  errorData?: { error?: string; errorDescription?: string; errorCodes?: number[] };
}

/** Too many wrong passwords: Entra locks the account for a while (AADSTS50053). */
export const isLocked = (error: FlowError | undefined) => !!error?.errorData?.errorCodes?.includes(50053);

/** The flow sat too long between steps and has to start again. */
export const isExpired = (error: FlowError | undefined) => !!error?.isTokenExpired?.();

/** Entra wants Microsoft's own pages for this account (a method these screens don't offer). */
export const needsRedirect = (error: FlowError | undefined) => !!error?.isRedirectRequired?.();

/** Anything else: one plain sentence, never the raw Entra text. */
export const somethingWrong = "Something went wrong on our side. Try again in a moment.";

/** The same sentence, with what Entra or the SDK said written to the browser console, for support. */
export function wrong(step: string, error: FlowError | undefined): string {
  const data = error?.errorData as (FlowError["errorData"] & { subError?: string; correlationId?: string }) | undefined;
  console.warn(`AutoVouch sign-in: ${step} failed`, data ? { error: data.error, description: data.errorDescription, codes: data.errorCodes, subError: data.subError, correlationId: data.correlationId } : "no error detail");
  return somethingWrong;
}

/** Entra's password rules, as the hint under a new-password field says them. */
export const passwordHint = "8+ characters, mixing upper and lower case, numbers or symbols.";
export const passwordRefused = "Pick a stronger password: 8 or more characters, with at least three of upper case, lower case, numbers and symbols.";

/** 0 to 4: length and the mix of character kinds, for the strength bar. */
export function passwordStrength(password: string) {
  if (!password) return 0;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(password)).length;
  if (password.length < 8) return 1;
  if (kinds < 3) return 2;
  return password.length >= 12 ? 4 : 3;
}

/** Where a sign-up stands after a step: a code to enter, done (sign in next), or refused. */
export type SignUpOutcome =
  | { kind: "code"; state: SignUpCodeRequiredState }
  | { kind: "done"; state: SignUpCompletedState }
  | { kind: "refused"; error: SignUpErrorLike | undefined };

export interface SignUpErrorLike extends FlowError {
  isUserAlreadyExists?: () => boolean;
  isInvalidUsername?: () => boolean;
  isInvalidPassword?: () => boolean;
  isInvalidCode?: () => boolean;
}

interface AnySignUpResult {
  state: unknown;
  error?: SignUpErrorLike;
  isCodeRequired?: () => boolean;
  isPasswordRequired?: () => boolean;
  isAttributesRequired?: () => boolean;
  isCompleted?: () => boolean;
}

/**
 * Walks a sign-up through whatever Entra asks next, in its order: a password or the attributes are answered from
 * what the form already holds; a code, or the end, comes back to the screen.
 */
export async function advanceSignUp(first: unknown, values: { password: string; displayName: string; company: string }): Promise<SignUpOutcome> {
  let result = first as AnySignUpResult;
  for (let step = 0; step < 4; step++) {
    if (result.isCompleted?.()) return { kind: "done", state: result.state as SignUpCompletedState };
    if (result.isCodeRequired?.()) return { kind: "code", state: result.state as SignUpCodeRequiredState };
    if (result.isPasswordRequired?.()) {
      result = (await (result.state as SignUpPasswordRequiredState).submitPassword(values.password)) as unknown as AnySignUpResult;
      continue;
    }
    if (result.isAttributesRequired?.()) {
      const state = result.state as SignUpAttributesRequiredState;
      const filled = knownAttributes(state.getRequiredAttributes(), values);
      if (!filled) return { kind: "refused", error: undefined };
      result = (await state.submitAttributes(filled)) as unknown as AnySignUpResult;
      continue;
    }
    break;
  }
  return { kind: "refused", error: result.error };
}

/** Sign-up attributes Entra asks for that these screens fill in themselves: the name, and an older tenant's Company. */
export function knownAttributes(required: { name: string }[], values: { displayName: string; company: string }): Record<string, string> | null {
  const filled: Record<string, string> = {};
  for (const attribute of required) {
    if (attribute.name === "displayName") filled.displayName = values.displayName;
    else if (/company/i.test(attribute.name)) filled[attribute.name] = values.company;
    else return null;
  }
  return filled;
}
